import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ToastrService } from 'ngx-toastr';

import {
  BulkFileDataOut,
  BulkFileGetAllDataIn,
  Client,
  FileParameter,
  FileType,
  PageInfo,
  PermissionsEnum,
  SortingType,
} from '../../../api/api-reference';
import { DragAndDropDirective } from '../../../directives/drag-and-drop.directive';
import { generateFileNameAppendix } from '../../../helper/download-filename';
import { AuthService } from '../../../services/auth.service';
import { FileService } from '../../../services/file.service';
import { PaginationComponent } from '../../core/components/pagination/pagination.component';
import { SignalRService } from '../../../services/signalr.service';
import { SignalRBaseComponent } from '../../../services/signalr-base.component';
import { ConfirmDialogComponent } from '../../core/components/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-upload',
  standalone: true,
  imports: [
    AngularSvgIconModule,
    FormsModule,
    CommonModule,
    PaginationComponent,
    TranslateModule,
    RouterLink,
    ConfirmDialogComponent
  ],
  providers: [DragAndDropDirective],
  templateUrl: './upload.component.html',
  styleUrl: './upload.component.scss',
})
export class UploadComponent extends SignalRBaseComponent implements OnInit {
  @ViewChild('fileInput') fileInput!: ElementRef;

  public currentPage: number = 1;
  public numberOfItems: number = 0;
  public lastPageSize: number = 1;
  public pageSize: number = 10;
  public pageNumber: number = 1;
  requestData = new BulkFileGetAllDataIn({
    pageInfo: new PageInfo({ pageSize: this.pageSize, page: this.pageNumber }),
    sorting: SortingType.Descending,
    fileType: FileType.DeviceImport,
  });
  data: BulkFileDataOut[] | undefined = [];
  activeRow: number | null = null;
  entity: string = '';
  backLink: string ='';
  showProcessModal = false;
  pendingProcessFileId: string | null = null;

  get timezoneString(): string {
    const offset = Number(localStorage.getItem('tz') ?? 0);
    const sign = offset >= 0 ? '+' : '-';
    const abs = Math.abs(offset);
    const h = Math.floor(abs / 60).toString().padStart(2, '0');
    const m = (abs % 60).toString().padStart(2, '0');
    return `${sign}${h}${m}`;
  }

  constructor(
    private toastr: ToastrService,
    private client: Client,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private fileService: FileService,
    signalRService: SignalRService
  ) {
    super(signalRService);

    this.route.paramMap.subscribe((params) => {
      this.entity = params.get('entity')!;
      this.initPermissions();
    });
  }

  /** Each import describes its own sample file, the sheets have nothing in common. */
  get sampleFileText(): string {
    switch (this.entity) {
      case 'user':
        return 'SAMPLEFILEDOWNLOAD_USER';
      case 'version':
        return 'SAMPLEFILEDOWNLOAD_VERSION';
      case 'token':
        return 'SAMPLEFILEDOWNLOAD_TOKEN';
      case 'event-codes':
        return 'SAMPLEFILEDOWNLOAD_EVENTCODES';
      default:
        return 'SAMPLEFILEDOWNLOAD';
    }
  }

  override async ngOnInit(): Promise<void> {
    await super.ngOnInit();
    this.backLink = this.router.url === '/upload/device'
      ? '/devices/all-devices'
      : this.router.url === '/upload/token'
      ? '/remote-command/token-load/token-load'
      : '/settings/crud/meter-model';

    this.reloadTable(1);
  }

  private initPermissions() {
    if (this.entity == 'device') {
      if (!this.authService.isAuthorized([PermissionsEnum.Device_bulk_upload])) {
        this.redirectUnauthorized();
        return;
      }
      this.requestData.fileType = FileType.DeviceImport;
    } else if (this.entity == 'user') {
      if (!this.authService.isAuthorized([PermissionsEnum.Settings_team_member_bulk_upload])) {
        this.redirectUnauthorized();
        return;
      }
      this.requestData.fileType = FileType.UserImport;
    } else if (this.entity == 'version') {
      if (!this.authService.isAuthorized([PermissionsEnum.Settings_crud_metermodel_modify])) {
        this.redirectUnauthorized();
        return;
      }
      this.requestData.fileType = FileType.VersionImport;
    } else if (this.entity == 'event-codes') {
      if (!this.authService.isAuthorized([PermissionsEnum.Settings_crud_metermodel_modify])) {
        this.redirectUnauthorized();
        return;
      }
      this.requestData.fileType = FileType.EventCodeImport;
    } else if(this.entity == 'token'){
      if (!this.authService.isAuthorized([PermissionsEnum.Remotecommands_tokenload_execute]) &&
          !this.authService.isAuthorized([PermissionsEnum.Remotecommands_clearcredit_execute]) &&
          !this.authService.isAuthorized([PermissionsEnum.Remotecommands_cleartamper_execute])) {
        this.redirectUnauthorized();
        return;
      }
      this.requestData.fileType = FileType.TokenImport;
    }else {
      this.toastr.warning('Unsupported entity type for bulk import.');
      this.router.navigate(['/']);
    }
  }

  private redirectUnauthorized() {
    this.router.navigate(['/']);
    this.toastr.error("You don't have permission to access this route.");
  }

  protected override registerHandlers(): void {
    this.registerSignalREvent('bulk-import', (message) => {
      const item = this.data?.find(i => i.id === message.fileId);
      if (item) {
        item.status = message.status;
        item.processedRows = message.processedRows;
        item.totalRows = message.totalRows;
      }
    });

    // Silence broadcast notifications
    this.registerSignalREvent('online-status', () => {});
  }

  pageChange(value: number) {
    this.currentPage = value;
    this.reloadTable(this.currentPage);
  }

  reloadTable(page?: number) {
    this.pageNumber = page || 0;
    if (page) this.requestData!.pageInfo!.page = this.pageNumber;

    this.client.queryFile(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data;
        this.numberOfItems = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfItems / this.pageSize);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  setActiveRow(rowIndex: number | null) {
    this.activeRow = this.activeRow === rowIndex ? null : rowIndex;
  }

  downloadReport(idFile: string) {
    const fileNameApendix = generateFileNameAppendix();

    this.client
      .downloadImportReport(idFile, this.requestData.fileType)
      .subscribe({
        next: (response) => {
          if (response.data) {
            this.fileService.downloadFile(
              response.data,
              response.fileName || `ImportReport${fileNameApendix}`,
            );
          } else {
            this.toastr.error('No file content received');
          }
        },
        error: (err) => {
          this.toastr.error(err);
        },
      });
    this.setActiveRow(null)
  }
  openProcessModal(idFile: string) {
    this.setActiveRow(null);
    if (this.entity === 'device' || this.entity === 'user') {
      this.pendingProcessFileId = idFile;
      this.showProcessModal = true;
    } else {
      this.processFile(idFile);
    }
  }

  cancelProcessModal() {
    this.showProcessModal = false;
    this.pendingProcessFileId = null;
  }

  confirmProcess(override: boolean) {
    if (!this.pendingProcessFileId) return;
    this.processFile(this.pendingProcessFileId, override);
    this.cancelProcessModal();
  }

  processFile(idFile: string, override: boolean = true) {
    this.client.processFile(idFile, override).subscribe({
      next: (r) => {
        this.toastr.success(r);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  downloadFile(idFile: string) {
    const fileNameApendix = generateFileNameAppendix();

    this.client
      .downloadImportFile(idFile, this.requestData.fileType)
      .subscribe({
        next: (response) => {
          if (response.data) {
            this.fileService.downloadFile(
              response.data,
              response.fileName || `ImportedFile${fileNameApendix}`,
            );
          } else {
            this.toastr.error('No file content received');
          }
        },
        error: (err) => {
          this.toastr.error(err);
        },
      });
    this.setActiveRow(null)
  }

  getTemplate() {
    this.client.downloadImportTemplate(this.requestData.fileType).subscribe({
      next: (response) => {
        if (response.data) {
          this.fileService.downloadFile(
            response.data,
            response.fileName || this.requestData.fileType + '_Template.xlsx',
          );
        } else {
          this.toastr.error('No file content received');
        }
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (input?.files?.length) {
      const file = input.files[0];

      if (
        !file.name.endsWith('.xlsx') ||
        file.type !==
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ) {
        this.toastr.error('Please select a valid XLSX file.');
      } else {
        // Creating object `FileParameter`
        const fileParameter: FileParameter = {
          data: file,
          fileName: file.name,
        };

        // Sending file to the server
        this.client
          .uploadImportFile(this.requestData.fileType, fileParameter)
          .subscribe({
            next: (response) => {
              this.toastr.success('Successfuly imported file.');
              this.reloadTable(1);
              this.fileInput.nativeElement.value = '';
              console.log('Response:' + response);
            },
            error: (err) => {
              this.toastr.error(err);
            },
          });
      }

      setTimeout(() => {
        input.value = '';
      }, 0);
    }
  }
}
